import { useEffect } from 'react'

import { recoverFromStaleHardNavigation } from 'lib/tracking/helpers/recoverFromStaleHardNavigation/recoverFromStaleHardNavigation'
import { recoverFromChunkLoadError } from 'lib/tracking/helpers/recoverFromChunkLoadError/recoverFromChunkLoadError'

/**
 * The app's global safety net for the two ways a superseded build breaks a navigation: Next's "hard
 * navigate to the same URL" invariant (see `recoverFromStaleHardNavigation`) and a chunk miss for a
 * bundle the deploy rotated off the CDN (see `recoverFromChunkLoadError`). Both are thrown
 * asynchronously outside React's render pass — by the router and by the bundler's chunk loader
 * respectively — so no error boundary catches them, and they only surface on `window` as an
 * unhandled rejection (or, defensively, an error event).
 *
 * Claiming a chunk miss here also pre-empts Next's own fallback, which strands the user for
 * `MS_MAX_IDLE_DELAY` (3.8s) while the route load times out into a hard navigation.
 *
 * `preventDefault` on a claimed error marks it handled to keep it out of the console; RUM's own
 * capture is silenced separately by the `beforeSend` noise filters.
 */
export const useRecoverFromStaleDeployErrors = () => {
  useEffect(() => {
    const recover = (error: unknown): boolean =>
      recoverFromStaleHardNavigation({ error }) || recoverFromChunkLoadError({ error })

    const handleRejection = (event: PromiseRejectionEvent) => {
      if (recover(event.reason)) {
        event.preventDefault()
      }
    }

    const handleError = (event: ErrorEvent) => {
      if (recover(event.error ?? event.message)) {
        event.preventDefault()
      }
    }

    window.addEventListener('unhandledrejection', handleRejection)
    window.addEventListener('error', handleError)

    return () => {
      window.removeEventListener('unhandledrejection', handleRejection)
      window.removeEventListener('error', handleError)
    }
  }, [])
}
